import { useEffect, useRef } from "react";

/**
 * useDialogA11y — shared accessibility behavior for the bottom-sheet
 * modals across the app (AddRecipeSheet, IngredientEditor,
 * AddToMealPlanSheet).
 *
 * What it does when `open` is true:
 *   1. Restores focus to the previously-focused element on close.
 *   2. Listens for Escape and calls onClose.
 *   3. Traps Tab focus inside the dialog container.
 *   4. Auto-focuses the first focusable element on open (so a screen
 *      reader announces the dialog content).
 *
 * Caller responsibility:
 *   - Apply ref={containerRef} to the dialog content div.
 *   - Pass role="dialog", aria-modal="true", aria-labelledby={titleId}
 *     props on the same div.
 *   - Mark the visible heading inside the dialog with id={titleId}.
 *
 * Example:
 *   const titleId = "mealplan-title";
 *   const { containerRef } = useDialogA11y({ open, onClose, titleId });
 *   return open ? (
 *     <div role="dialog" aria-modal="true" aria-labelledby={titleId} ref={containerRef}>
 *       <h2 id={titleId}>Add to meal plan</h2>
 *       ...
 *     </div>
 *   ) : null;
 */

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useDialogA11y({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
  titleId?: string; // accepted for API symmetry but used by caller as the id ref
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Snapshot focused element when dialog opens, restore when it closes.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null;
    // Auto-focus the first focusable element after a tick so layout is stable.
    const tick = setTimeout(() => {
      const first = containerRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    }, 0);
    return () => {
      clearTimeout(tick);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [open]);

  // Escape key + focus trap.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !containerRef.current) return;
      const focusables = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("disabled"));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  return { containerRef };
}
